<template>
  <div class="registration" v-if="model.title != null">
    <vue-title :title="model.title"></vue-title>
    <h1 class="section-title">{{ model.title }}</h1>

    <Form v-slot="{ validate, setErrors, setFieldError }">
      <form ref="form" @submit.prevent="validateBeforeSubmit(validate, setErrors, setFieldError)">
        <div v-if="model.showError">
          <div class="validation-summary-errors alert alert-danger fade show" role="alert" data-valmsg-summary="true" data-test-element="idp-alert-danger-generic">
            <i class="material-icons">warning</i>
            <span>{{ model.errorMessage }}</span>
          </div>
          <button type="button" v-on:click="loginCancel(cancelLink)" name="Action:RedirectToLogin" class="button" :disabled="disabled">{{ $t('shared:action:back') }}</button>
        </div>
        <div v-else>
          <fieldset>
            <div class="panel-message" v-html="model.instructions"></div>

            <div :class="{'mb-4': model.isAddNewPhoneEnabled}">
              <label>
                <span class="item label">{{ $t('identityProvider:login:setupOTPSelectPhone') }}</span>
              </label>

              <!-- existing phone list (unchanged) -->
              <div class="form-group" v-for="(option,index) in model.availablePhoneNumbers" :key="option.id">
                <input type="radio"
                       class="mr-1"
                       name="phoneNumber"
                       :value="option.phoneNumber"
                       :id="parseInt(option.countryCodeID) > 1 ? option.countryCodeID : '1'"
                       :data-id="option.countryCode"
                       :data-pid="option.id"
                       :data-forsms="option.registeredForSMS"
                       :checked="index == 0"
                       @change="ddlBindPhoneNumber()" />
                <label class="displayInline" :for="option.id"> +{{ option.countryCode }} {{ option.phoneNumber }}</label>
              </div>

              <!-- add new phone (validate only when selected) -->
              <div v-if="model.isAddNewPhoneEnabled && model.phoneComplexity !== null">
                <input type="radio" class="mr-1" id="addnewradio" name="phoneNumber" @change="txtBindPhoneNumber()">
                <select id="ddlCountryCode" class="control countryCode displayInline" disabled>
                  <option :value="country.countryCodeID"
                          :data-id="country.countryCode"
                          v-for="country in model.countries"
                          :key="country.countryCodeID"
                          v-html="returnCountryCodeAndCountryName(country.countryCode , country.countryName)">
                  </option>
                </select>

                <Field name="phoneNo" :rules="phoneRules" v-model="insertmodel.phoneNumber" v-slot="{ field, meta }">
                  <input v-bind="field"
                         type="text"
                         id="txtPhoneNumber"
                         name="phoneNo"
                         class="control input-text displayInline"
                         :disabled="!addNewSelected"
                         placeholder="XXX XXX-XXXX"
                         autocomplete="off"
                         :maxlength="15"
                         :class="{ 'input-validation-error': submitted && !meta.valid }" />
                </Field>

                <ErrorMessage v-if="submitted" name="phoneNo" v-slot="{ message }">
                  <span class="field-validation-error" role="alert">{{ message }}</span>
                </ErrorMessage>
              </div>
            </div>

            <div class="content-panel-row">
              <label>
                <span class="item label">{{ $t('identityProvider:login:setupOTPpreferredNotificationMethod') }}</span>
              </label>
              <div class="form-group">
                <label class="displayInline">
                  <input type="radio" name="preferredNotificationMethod" id="smsPerferredNotifyMethod" class="mr-1" value="sms" checked="checked" />
                  {{ $t('identityProvider:login:setupOTPReceiveText') }}
                </label>
              </div>
              <div class="form-group">
                <label class="displayInline">
                  <input type="radio" name="preferredNotificationMethod" id="callPerferredNotifyMethod" class="mr-1" value="ivr" />
                  {{ $t('identityProvider:login:setupOTPReceiveCall') }}
                </label>
              </div>
            </div>

            <div class="form-actions">
              <button type="submit" class="button" :disabled="disabled">{{$t('shared:action:continue')}}</button>
              <a v-on:click="loginCancel(cancelLink)" class="button-secondary contextual-link" :data-test-element="'security-question-cancel'">{{ $t('shared:action:cancel') }}</a>
            </div>
          </fieldset>
        </div>
      </form>
    </Form>
  </div>
</template>

<script>
  import redirectHandler from 'utils/redirect';
  import ContextualHelp from 'components/popper-help';
  import $ from 'jquery';
  import { Form, Field, ErrorMessage } from 'vee-validate';
  import { applyServerErrorsFromAxios, focusFirstInvalid } from 'utils/validation-mixin';

  export default {
    name: 'SelectDeliveryMethod',
    components: {
      'contextual-help-component': ContextualHelp,
      Form,
      Field,
      ErrorMessage
    },

    props: {
      formAction: {
        default: 'authentication/setupphone',
        type: String
      },
      cancelLink: {
        default: 'authentication/logincancel',
        type: String
      }
    },

    data: () => ({
      model: {},
      insertmodel: {
        id: null,
        phoneNumber: null,
        countryCodeID: null,
        nickName: null,
        isDefault: false,
        defaultDeliveryMethod: null
      },
      countryCodeID: null,
      countryCode: null,
      phoneNumber: null,
      phoneId: null,
      action: null,
      submitted: false,
      addNewSelected: false,
      disabled: false
    }),

    computed: {
      phoneRules() {
        if (!this.addNewSelected || !this.model?.phoneComplexity) {
          return {}; // no validation when not adding new
        }
        const c = this.model.phoneComplexity;
        const rules = { required: true };
        if (typeof c.phoneMinimumLength === 'number') rules.min = c.phoneMinimumLength;
        if (typeof c.phoneMaximumLength === 'number') rules.max = c.phoneMaximumLength;
        if (c.phonePattern) {
          try { rules.regex = new RegExp(c.phonePattern); } catch { /* ignore invalid regex */ }
        }
        return rules;
      }
    },

    methods: {
      async validateBeforeSubmit(validate, setErrors, setFieldError) {
        this.submitted = true;

        const result = await validate();
        const isValid = result === true || result?.valid === true;

        if (isValid) {
          await this.post(setErrors, setFieldError);
        } else {
          focusFirstInvalid(this.$refs.form);
        }
      },

      returnCountryCodeAndCountryName(code, name) {
        var len = code.toString().length;
        var result = '+' + code + '<pre>';
        for (var i = len; i < 7; i++) {
          result += '&nbsp;&nbsp;';
        }
        var countryCodeOption = result + '</pre>' + name;
        return countryCodeOption;
      },

      isEmptyorNull(value) {
        return (value == 'undefined' || value == null || value == '');
      },

      disabledSMSPreferredNotificationMethod() {
        var value = $("input[name='phoneNumber']:checked").attr('data-forsms');
        var disabled = value != 'true' ? true : false;
        $('#smsPerferredNotifyMethod').attr('disabled', disabled);
        if (disabled) {
          $('#callPerferredNotifyMethod').prop('checked', true);
        } else {
          $('#smsPerferredNotifyMethod').prop('checked', true);
        }
      },

      ddlBindPhoneNumber() {
        $('#ddlCountryCode').attr('disabled', true);
        $('#txtPhoneNumber').val('').attr('disabled', true);
        this.addNewSelected = false;
        if (this.formAction.indexOf('setupphone') == -1) {
          this.disabledSMSPreferredNotificationMethod();
        }
      },

      txtBindPhoneNumber() {
        $('#ddlCountryCode').removeAttr('disabled');
        $('#txtPhoneNumber').removeAttr('disabled');
        $('#smsPerferredNotifyMethod').attr('disabled', false);
        $('#smsPerferredNotifyMethod').prop('checked', true);
        this.addNewSelected = true;
      },

      async post(setErrors, setFieldError) {
        try {
          var countryCodeIDAttr = $("input[name='phoneNumber']:checked").attr('id');
          var countryCodeAttr = $("input[name='phoneNumber']:checked").attr('data-id');
          var phoneIdAttr = $("input[name='phoneNumber']:checked").attr('data-pid');

          if ($('#addnewradio').prop('checked')) {
            this.countryCodeID = $('#ddlCountryCode option:selected').val();
            this.countryCode = $('#ddlCountryCode option:selected').attr('data-id');
            this.phoneNumber = $('#txtPhoneNumber').val();
          }

          if (this.isEmptyorNull(this.phoneNumber)) {
            this.phoneNumber = $("input[name='phoneNumber']:checked").val();
          }

          if (this.isEmptyorNull(this.countryCodeID)) {
            this.countryCodeID = countryCodeIDAttr;
            this.countryCode = countryCodeAttr;
            this.phoneId = phoneIdAttr;
          }

          this.insertmodel.countryCodeID = parseInt(this.countryCodeID);
          this.insertmodel.countryCode = parseInt(this.countryCode);
          this.insertmodel.id = this.phoneId;
          this.insertmodel.phoneNumber = this.phoneNumber.replace(/[_\W]+/g, '');
          this.insertmodel.defaultDeliveryMethod = $("input[name='preferredNotificationMethod']:checked").val();

          let response = await this.$http.post(this.action, this.insertmodel);
          this.model = response.data;
          if (!response.data.showError) {
            redirectHandler.checkAndRedirect(response, this.$router);
          }
        } catch (error) {
          const form = this.$refs.form;

          // Map ASP.NET Core 400 ModelState to fields
          if (applyServerErrorsFromAxios(error, { setErrors, setFieldError })) {
            focusFirstInvalid(form);
            return;
          }

          // Prefer legacy summary handling if server returns same shape
          if (this.validateResponse?.(form, error.response)) {
            return;
          }          

          throw error;
        } finally {
          // no-op; keep button state managed via disabled if needed
        }
      },

      async loginCancel(path) {
        let response = await this.$http.post(path);
        redirectHandler.checkAndRedirect(response, this.$router);
      }
    },

    async created() {
      try {
        this.action = this.formAction;
        let response = await this.$http.get(this.action);
        this.model = response.data;
      } catch (error) {
        const form = this.$refs.form;
        if (this.validateResponse?.(form, error.response)) {
          return;
        }
        throw error;
      }
    },

    async updated() {
      if (this.formAction.indexOf('setupphone') == -1) {
        this.disabledSMSPreferredNotificationMethod();
      }
    }
  };
</script>